import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, ChevronDown, ChevronUp, AlertTriangle, User, DollarSign } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import auctionEmailService from '../../../services/auctionEmailService';

const priorityConfig = {
  critical: { label: 'Critical', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
  high: { label: 'High', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
  medium: { label: 'Medium', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
  low: { label: 'Low', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20' },
};

const statusConfig = {
  disputed: { label: 'Disputed', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
  pending: { label: 'Pending', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
  held: { label: 'Held', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  released: { label: 'Released', color: 'text-green-400 bg-green-400/10 border-green-400/20' },
  refunded: { label: 'Refunded', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20' },
  cancelled: { label: 'Cancelled', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20' },
};

export default function DisputeQueue() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadDisputes();
  }, []);

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('escrow_transactions')?.select(`
          id,
          amount,
          status,
          payment_status,
          buyer_confirmed,
          seller_confirmed,
          created_at,
          updated_at,
          buyer:user_profiles!escrow_transactions_buyer_id_fkey(full_name, email),
          seller:user_profiles!escrow_transactions_seller_id_fkey(full_name, email),
          marketplace_listings!escrow_transactions_listing_id_fkey(title)
        `)?.order('created_at', { ascending: false })?.limit(50);

      if (!error) {
        setDisputes(data || []);
      }
    } catch (err) {
      console.error('Error loading disputes:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = disputes?.filter(d => statusFilter === 'all' || d?.status === statusFilter);

  const handleResolve = async (id, resolution) => {
    setActionLoading(id);
    try {
      const newStatus = resolution === 'refund' ? 'refunded' : 'released';
      const { error } = await supabase?.from('escrow_transactions')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', id);
      if (!error) {
        const dispute = disputes?.find(d => d?.id === id);
        setDisputes(prev => prev?.map(d => d?.id === id ? { ...d, status: newStatus } : d));
        setExpanded(null);
        // Send dispute resolved emails to buyer and seller
        if (dispute?.buyer?.id || dispute?.seller?.id) {
          auctionEmailService?.sendDisputeResolvedEmail(
            dispute?.buyer?.id,
            dispute?.seller?.id,
            {
              id,
              itemTitle: dispute?.marketplace_listings?.title || 'Item',
              amount: dispute?.amount
            },
            resolution
          );
        }
      }
    } catch (err) {
      console.error('Resolve error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getPriority = (dispute) => {
    if (dispute?.status === 'disputed') return 'critical';
    if (parseFloat(dispute?.amount) > 10000) return 'high';
    if (parseFloat(dispute?.amount) > 1000) return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3]?.map(i => <div key={i} className="h-20 bg-gray-800 rounded-xl border border-gray-700" />)}
      </div>
    );
  }

  const statusCounts = {
    disputed: disputes?.filter(d => d?.status === 'disputed')?.length,
    held: disputes?.filter(d => d?.status === 'held')?.length,
    pending: disputes?.filter(d => d?.status === 'pending')?.length,
    released: disputes?.filter(d => d?.status === 'released')?.length,
  };

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Disputed', count: statusCounts?.disputed, color: 'text-red-400', filter: 'disputed' },
          { label: 'Held', count: statusCounts?.held, color: 'text-blue-400', filter: 'held' },
          { label: 'Pending', count: statusCounts?.pending, color: 'text-yellow-400', filter: 'pending' },
          { label: 'Released', count: statusCounts?.released, color: 'text-green-400', filter: 'released' },
        ]?.map(s => (
          <button
            key={s?.label}
            onClick={() => setStatusFilter(statusFilter === s?.filter ? 'all' : s?.filter)}
            className={`bg-gray-800 border rounded-xl p-3 text-center hover:border-gray-600 transition-colors ${statusFilter === s?.filter ? 'border-blue-500/40' : 'border-gray-700'}`}
          >
            <div className={`text-2xl font-bold ${s?.color}`}>{s?.count}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s?.label}</div>
          </button>
        ))}
      </div>
      {/* Dispute List */}
      <div className="space-y-2">
        {filtered?.map(dispute => {
          const priority = getPriority(dispute);
          return (
            <div key={dispute?.id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-800/80 transition-colors"
                onClick={() => setExpanded(expanded === dispute?.id ? null : dispute?.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-white text-sm">{dispute?.marketplace_listings?.title || 'Transaction'}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${priorityConfig?.[priority]?.color}`}>
                      {priorityConfig?.[priority]?.label}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusConfig?.[dispute?.status]?.color || 'text-gray-400 bg-gray-400/10 border-gray-400/20'}`}>
                      {statusConfig?.[dispute?.status]?.label || dispute?.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><User size={10} /> {dispute?.buyer?.full_name || 'Buyer'} vs {dispute?.seller?.full_name || 'Seller'}</span>
                    <span className="flex items-center gap-1"><DollarSign size={10} /> ${parseFloat(dispute?.amount || 0)?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="text-gray-500 flex-shrink-0">
                  {expanded === dispute?.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {expanded === dispute?.id && (
                <div className="border-t border-gray-700 p-4 space-y-4 bg-gray-900/30">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-gray-500 mb-1">Buyer</div>
                      <div className="text-gray-200">{dispute?.buyer?.full_name || dispute?.buyer?.email || '—'}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-gray-500 mb-1">Seller</div>
                      <div className="text-gray-200">{dispute?.seller?.full_name || dispute?.seller?.email || '—'}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-gray-500 mb-1">Created</div>
                      <div className="text-gray-200">{new Date(dispute.created_at)?.toLocaleDateString()}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-gray-500 mb-1">Payment Status</div>
                      <div className="text-gray-200 capitalize">{dispute?.payment_status || '—'}</div>
                    </div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle size={14} className="text-orange-400" />
                      <span className="text-xs font-semibold text-orange-400">Confirmation Status</span>
                    </div>
                    <p className="text-sm text-gray-300">
                      Buyer confirmed: {dispute?.buyer_confirmed ? '✅' : '❌'} &nbsp;|&nbsp;
                      Seller confirmed: {dispute?.seller_confirmed ? '✅' : '❌'}
                    </p>
                  </div>
                  {dispute?.status !== 'released' && dispute?.status !== 'refunded' && dispute?.status !== 'cancelled' && (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleResolve(dispute?.id, 'release')}
                        disabled={actionLoading === dispute?.id}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-lg text-xs hover:bg-green-600/30 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle size={12} /> Release Funds
                      </button>
                      <button
                        onClick={() => handleResolve(dispute?.id, 'refund')}
                        disabled={actionLoading === dispute?.id}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-600/30 text-blue-400 rounded-lg text-xs hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle size={12} /> Issue Refund
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare size={32} className="mx-auto mb-3 opacity-30" />
            <p>No transactions in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}
